/**
 * @file Contributor UI — a2VehicleLayout.
 */

import type { RawMat } from "../hooks/useA2Prefill";

/** Maximum vehicle rows allowed per background raw material (Form A2). */
export const A2_MAX_VEHICLES_PER_RAW_MATERIAL = 3;

/** Exported function — total Vehicle Rows. */
export function totalVehicleRows(counts: number[]): number {
  return counts.reduce((a, b) => a + Math.max(0, b), 0);
}

/** Which background raw-material index (0..R-1) this flat vehicle row belongs to. */
export function materialIndexForVehicleFlatRow(
  flatIdx: number,
  counts: number[]
): number {
  let cursor = 0;
  for (let m = 0; m < counts.length; m++) {
    const c = Math.max(0, counts[m]);
    cursor += c;
    if (flatIdx < cursor) return m;
  }
  return Math.max(0, counts.length - 1);
}

/** Flat index where a new vehicle row should be inserted for material `m` (append within that block). */
export function insertIndexForNewVehicleAtMaterial(
  m: number,
  counts: number[]
): number {
  if (m < 0 || m >= counts.length) return totalVehicleRows(counts);
  return counts.slice(0, m).reduce((a, b) => a + Math.max(0, b), 0) + Math.max(0, counts[m]);
}

function resolveMaterialIndexForVehicleItem(
  item: Record<string, unknown>,
  normalizedRaw: RawMat[],
  validCatalogRawMaterialIds: Set<number>,
  catalogNameToId: Map<string, number>,
  normalizeKey: (s: string) => string
): number {
  const R = normalizedRaw.length;
  if (R <= 0) return 0;

  const fromItem = item?.rawMaterialId ?? item?.raw_material_id;
  const n = Number(fromItem);
  if (Number.isFinite(n) && n > 0) {
    for (let m = 0; m < R; m++) {
      const rm = normalizedRaw[m];
      if (rm.catalogId === n) return m;
      if (
        validCatalogRawMaterialIds.size > 0 &&
        validCatalogRawMaterialIds.has(n) &&
        rm.id === n
      ) {
        return m;
      }
    }
    for (let m = 0; m < R; m++) {
      if (normalizedRaw[m].id === n) return m;
    }
  }

  const nameRaw =
    item?.rawMaterialName ??
    item?.raw_material_name ??
    item?.materialName ??
    "";
  const name =
    typeof nameRaw === "string" ? nameRaw.trim() : String(nameRaw ?? "");
  if (name) {
    const byName = catalogNameToId.get(normalizeKey(name));
    if (byName != null) {
      for (let m = 0; m < R; m++) {
        if (normalizedRaw[m].catalogId === byName) return m;
      }
    }
    for (let m = 0; m < R; m++) {
      if (normalizeKey(normalizedRaw[m].name) === normalizeKey(name)) return m;
    }
  }

  return 0;
}

/**
 * Group saved A2 vehicles per background raw material (order matches `normalizedRaw`).
 * Each material gets at least one slot for the form.
 */
export function buildA2VehiclePrefillLayout(
  vehicleArr: unknown[] | undefined,
  normalizedRaw: RawMat[],
  validCatalogRawMaterialIds: Set<number>,
  catalogNameToId: Map<string, number>,
  normalizeRawMaterialNameKey: (s: string) => string
): { counts: number[]; flatItems: Record<string, unknown>[] } {
  const R = Math.max(1, normalizedRaw.length);
  if (!vehicleArr?.length) {
    return {
      counts: Array(R).fill(1),
      flatItems: Array.from({ length: R }, () => ({})),
    };
  }

  const buckets: Record<string, unknown>[][] = Array.from(
    { length: R },
    () => []
  );

  for (const raw of vehicleArr) {
    const item = (raw && typeof raw === "object" ? raw : {}) as Record<
      string,
      unknown
    >;
    const m = resolveMaterialIndexForVehicleItem(
      item,
      normalizedRaw,
      validCatalogRawMaterialIds,
      catalogNameToId,
      normalizeRawMaterialNameKey
    );
    buckets[m].push(item);
  }

  const counts = buckets.map((b) =>
    Math.min(A2_MAX_VEHICLES_PER_RAW_MATERIAL, Math.max(1, b.length))
  );
  const flatItems: Record<string, unknown>[] = [];
  for (let m = 0; m < R; m++) {
    for (let v = 0; v < counts[m]; v++) {
      flatItems.push(buckets[m][v] ?? {});
    }
  }

  return { counts, flatItems };
}

/** Type definition — A2Grouped Vehicle Slice. */
export type A2GroupedVehicleSlice = { vehicles?: unknown[] };

/** Grouped API: `rawMaterialGroups[m].vehicles` in background order. */
export function buildA2VehiclePrefillFromGrouped(
  orderedGroups: A2GroupedVehicleSlice[],
  R: number
): { counts: number[]; flatItems: Record<string, unknown>[] } {
  const safeR = Math.max(1, R);
  const groups: A2GroupedVehicleSlice[] =
    orderedGroups.length >= safeR
      ? orderedGroups.slice(0, safeR)
      : [
          ...orderedGroups,
          ...Array.from({ length: safeR - orderedGroups.length }, () =>
            ({} as A2GroupedVehicleSlice)
          ),
        ];

  const counts = groups.map((g) => {
    const n = Array.isArray(g.vehicles) ? g.vehicles.length : 0;
    return Math.min(
      A2_MAX_VEHICLES_PER_RAW_MATERIAL,
      Math.max(1, n)
    );
  });
  const flatItems: Record<string, unknown>[] = [];
  for (let m = 0; m < safeR; m++) {
    const vs = Array.isArray(groups[m].vehicles) ? groups[m].vehicles! : [];
    const c = counts[m];
    for (let v = 0; v < c; v++) {
      const raw = vs[v];
      flatItems.push(
        raw && typeof raw === "object"
          ? (raw as Record<string, unknown>)
          : {}
      );
    }
  }
  return { counts, flatItems };
}

const VEHICLE_CODE = "A2_VEHICLE_DETAILS";

/** All value keys to shift when inserting a vehicle row (includes RAW_MATERIALS `_label`). */
export function collectVehicleValueKeyPrefixes(
  formSubSections: { code?: string; fields: { name: string }[] }[] | undefined
): string[] {
  const sec = formSubSections?.find((s) => s.code === VEHICLE_CODE);
  if (!sec?.fields?.length) return [];
  return sec.fields.map((f) => f.name);
}

/** Exported function — shift A2Vehicle Values For Insert. */
export function shiftA2VehicleValuesForInsert(
  prev: Record<string, string>,
  insertAt: number,
  newRowCount: number,
  fieldNames: string[],
  code: string
): Record<string, string> {
  const next = { ...prev };
  const rowTag = `_${code}_row`;

  for (let i = newRowCount - 1; i > insertAt; i--) {
    const prevI = i - 1;
    for (const base of fieldNames) {
      const fromK = `${base}${rowTag}${prevI}`;
      const toK = `${base}${rowTag}${i}`;
      if (fromK in prev) next[toK] = prev[fromK] ?? "";
      else if (next[toK] !== undefined) delete next[toK];
      const fromL = `${fromK}_label`;
      const toL = `${toK}_label`;
      if (fromL in prev) next[toL] = prev[fromL] ?? "";
      else if (next[toL] !== undefined) delete next[toL];
    }
  }

  for (const base of fieldNames) {
    const k = `${base}${rowTag}${insertAt}`;
    next[k] = "";
    next[`${k}_label`] = "";
  }

  return next;
}

/** Remove flat vehicle row `removeAt` and shift later rows down; `oldRowCount` is total rows before removal. */
export function shiftA2VehicleValuesForRemove(
  prev: Record<string, string>,
  removeAt: number,
  oldRowCount: number,
  fieldNames: string[],
  code: string
): Record<string, string> {
  const newRowCount = oldRowCount - 1;
  if (
    newRowCount < 0 ||
    removeAt < 0 ||
    removeAt >= oldRowCount ||
    fieldNames.length === 0
  ) {
    return prev;
  }

  const next = { ...prev };
  const rowTag = `_${code}_row`;

  for (let i = removeAt; i < newRowCount; i++) {
    const fromI = i + 1;
    for (const base of fieldNames) {
      const fromK = `${base}${rowTag}${fromI}`;
      const toK = `${base}${rowTag}${i}`;
      next[toK] = prev[fromK] ?? "";
      const fromL = `${fromK}_label`;
      const toL = `${toK}_label`;
      if (fromL in prev) next[toL] = prev[fromL] ?? "";
      else delete next[toL];
    }
  }

  const lastOld = oldRowCount - 1;
  for (const base of fieldNames) {
    const k = `${base}${rowTag}${lastOld}`;
    delete next[k];
    delete next[`${k}_label`];
  }

  return next;
}
